import { Box, Tag, Text } from '@factorialco/deprecated-design-system'
import {
  GetBookingsDataTableQuery,
  ProjectionKind,
  useGetBookingsDataTable,
} from 'generated/resources/datatable.graphql'
import { LocationsForBookingsQuery } from 'generated/resources/graphql'
import uniq from 'lodash/uniq'

import { observer } from 'mobx-react'

import DataTable, { useDataTable } from 'modules/core/components/DataTable'
import { useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'
import { DataTableBooking } from 'modules/space_control/hooks/bookings/dataTableBooking'

import BookingsActionButtons from './BookingsPopoverMenu'
import EmployeeField from './EmployeeField'
import assigneeFilter from './assigneeFilter'
import dateFilter from './dateFilter'
import locationFilter from './locationFilter'
import spaceTypeFilter from './spaceTypeFilter'

export interface AssigneeFilterAccess {
  id: string
  firstName: string
  lastName: string
}

interface Props {
  locations: LocationsForBookingsQuery['locations']['locations']
  accesses: AssigneeFilterAccess[]
  canEditEverybody: boolean
  canEditSelf: boolean
  canCancelEverybody: boolean
  canCancelSelf: boolean
}

const Table = ({
  canEditEverybody,
  canCancelEverybody,
}: Pick<Props, 'canCancelEverybody' | 'canEditEverybody'>) => {
  const { data } = useDataTable<GetBookingsDataTableQuery>()

  useUnsafeAsyncData(async () => {
    const updateVerb = 'spacecontrol.booking.upsert'
    const cancelVerb = 'spacecontrol.booking.bulk_cancel'
    const employeeIds: string[] = data?.map((booking) => booking?.access?.employee?.id) ?? []

    if (employeeIds.length < 1) return Promise.resolve()
    const endpoints = []
    if (employeeIds.length > 0 && !canEditEverybody)
      endpoints.push(
        policyAuthorizations.authorizePolicies(
          updateVerb,
          uniq(employeeIds).map((id) => ({
            id: id,
            policyTargetClass: '::Employee',
          }))
        )
      )
    if (employeeIds.length > 0 && !canCancelEverybody)
      policyAuthorizations.authorizePolicies(
        cancelVerb,
        uniq(employeeIds).map((id) => ({
          id: id,
          policyTargetClass: '::Employee',
        }))
      )

    return Promise.all(endpoints)
  }, [data])

  return <DataTable.Table />
}

function BookingsDatatable({
  locations,
  accesses,
  canEditEverybody,
  canEditSelf,
  canCancelEverybody,
  canCancelSelf,
}: Props) {
  const { access } = useCurrentSession()
  const source = useGetBookingsDataTable([
    {
      identifier: 'date',
      title: i18n.t('space_control.my-bookings.datatable.headers.date'),
      kind: ProjectionKind.Custom,
      sortBy: 'date',
      render: ({ date }) => <Text.Normal>{date ? dateBackendToUI(date) : ''}</Text.Normal>,
      template: '{{ date }}',
    },
    {
      identifier: 'time',
      title: i18n.t('space_control.my-bookings.datatable.headers.time'),
      kind: ProjectionKind.Custom,
      hidden: false,
      render: ({ startsAt, endsAt }) => (
        <Text.Normal>{startsAt && endsAt ? `${startsAt} - ${endsAt}` : ''}</Text.Normal>
      ),
      template: '{{ startsAt }} - {{ endsAt }}',
    },
    {
      identifier: 'month',
      title: i18n.t('space_control.my-bookings.datatable.headers.month'),
      kind: ProjectionKind.Custom,
      hidden: true,
      render: ({ month }) => <Text.Normal>{month}</Text.Normal>,
      template: '{{ month }}',
    },
    {
      identifier: 'week',
      title: i18n.t('space_control.my-bookings.datatable.headers.week'),
      kind: ProjectionKind.Custom,
      hidden: true,
      render: ({ week }) => <Text.Normal>{week}</Text.Normal>,
      template: '{{ week }}',
    },
    {
      identifier: 'dayOfWeek',
      title: i18n.t('space_control.my-bookings.datatable.headers.day_of_week'),
      kind: ProjectionKind.Custom,
      hidden: true,
      render: ({ dayOfWeek }) => <Text.Normal>{dayOfWeek}</Text.Normal>,
      template: '{{ dayOfWeek }}',
    },
    {
      identifier: 'location',
      title: i18n.t('space_control.my-bookings.datatable.headers.location'),
      kind: ProjectionKind.Custom,
      template:
        '{{ workSpace_workArea_location_name }}, {{workSpace_workArea_name}}, {{workSpace_name}}',
      render: ({ workSpace_workArea_location_name, workSpace_workArea_name, workSpace_name }) => (
        <Box flexDirection="row" gap="s4" flexWrap="wrap">
          <Text.Normal weight="medium">{workSpace_name}</Text.Normal>
          <Text.Normal>{`${workSpace_workArea_location_name}, ${workSpace_workArea_name}`}</Text.Normal>
        </Box>
      ),
    },
    {
      identifier: 'workplace',
      title: i18n.t('space_control.my-bookings.datatable.headers.workplace'),
      kind: ProjectionKind.Custom,
      hidden: true,
      render: ({ workSpace_workArea_location_name }) => (
        <Text.Normal>{workSpace_workArea_location_name}</Text.Normal>
      ),
      template: '{{ workSpace_workArea_location_name }}',
    },
    {
      identifier: 'work_area',
      title: i18n.t('space_control.my-bookings.datatable.headers.work_area'),
      kind: ProjectionKind.Custom,
      hidden: true,
      render: ({ workSpace_workArea_name }) => <Text.Normal>{workSpace_workArea_name}</Text.Normal>,
      template: '{{ workSpace_workArea_name }}',
    },
    {
      identifier: 'space',
      title: i18n.t('space_control.my-bookings.datatable.headers.space'),
      kind: ProjectionKind.Custom,
      hidden: true,
      render: ({ workSpace_name }) => <Text.Normal>{workSpace_name}</Text.Normal>,
      template: '{{ workSpace_name }}',
    },

    {
      identifier: 'timeSlot',
      title: i18n.t('space_control.my-bookings.datatable.headers.time_slot'),
      kind: ProjectionKind.Custom,
      hidden: true,
      render: ({ timeSlot }) => <Text.Normal>{timeSlot ?? ''}</Text.Normal>,
      template: '{{ timeSlot }}',
    },
    {
      identifier: 'booked',
      title: i18n.t('space_control.my-bookings.datatable.headers.assignee'),
      kind: ProjectionKind.Custom,
      render: ({ access_fullName, access_avatar }) => (
        <EmployeeField fullName={access_fullName} avatar={access_avatar} />
      ),
      template: '{{ access_fullName }}',
    },
    {
      identifier: 'type',
      title: i18n.t('space_control.my-bookings.datatable.headers.type'),
      kind: ProjectionKind.Custom,
      render: ({ workSpace_workSpaceTypeName }) => (
        <Box inline>
          <Tag type="grey">{workSpace_workSpaceTypeName}</Tag>
        </Box>
      ),
      template: '{{ workSpace_workSpaceTypeName }}',
    },
    {
      identifier: 'occupiedCapacityPercent',
      title: i18n.t('space_control.my-bookings.datatable.headers.occupied_capacity_percent'),
      kind: ProjectionKind.Custom,
      hidden: false,
      render: ({ occupiedCapacityPercent }) => (
        <Text.Normal>{occupiedCapacityPercent ?? 0}%</Text.Normal>
      ),
      template: '{{ occupiedCapacityPercent }}',
    },
    {
      identifier: 'actions',
      kind: ProjectionKind.Decorative,
      render: (booking: DataTableBooking) => (
        <BookingsActionButtons
          booking={booking}
          canEditEverybody={canEditEverybody}
          canEditSelf={canEditSelf}
          canCancelEverybody={canCancelEverybody}
          canCancelSelf={canCancelSelf}
        />
      ),
      width: 120,
    },
  ])

  return (
    <Box padding="s16" flexDirection="row" alignItems="flexStart" gap="s16">
      <DataTable.Provider
        filters={[
          dateFilter(source),
          spaceTypeFilter(source),
          locationFilter(source, locations),
          assigneeFilter(source, accesses, access.id),
        ]}
        sort={{
          path: 'date',
        }}
        source={source}
      >
        <Box gap="s16">
          <DataTable.HeadBar
            exportFileName="bookings"
            onExportCallback={() =>
              track(trackingEvents.spaceControl.bookings.requestExportBooking)
            }
          />
          <Table canEditEverybody={canEditEverybody} canCancelEverybody={canCancelEverybody} />
        </Box>
      </DataTable.Provider>
    </Box>
  )
}

export default observer(BookingsDatatable)
